/**
 * Procedural audio engine — sub-bus mixer architecture.
 *
 *                    ┌────────────┐
 *                    │   master   │── destination
 *                    └─────▲──────┘
 *               ┌──────────┴──────────┐
 *               │                     │
 *         ┌─────┴─────┐         ┌─────┴─────┐
 *         │  sfxBus   │         │ musicBus  │
 *         └─────▲─────┘         └─────▲─────┘
 *               │                     │
 *         all SFX methods       reverb wet+dry
 *                                     │
 *                              Music voices
 *
 * Why sub-buses:
 *   - Independent gain control: music sits below SFX without each sound
 *     having to be tuned individually.
 *   - Easy to add ducking later (modulate musicBus gain when big SFX play).
 *   - Music gets its own reverb chain without affecting SFX clarity.
 *
 * All synthesis is on-the-fly — zero audio assets shipped.
 */
import { Music } from './Music';

const MUTE_KEY = 'cellsnake:muted';

function loadMuted(): boolean {
  try { return localStorage.getItem(MUTE_KEY) === '1'; }
  catch { return false; }
}
function saveMuted(muted: boolean): void {
  try { localStorage.setItem(MUTE_KEY, muted ? '1' : '0'); }
  catch { /* noop */ }
}

// ─── Mixer levels ───────────────────────────────────────────────────────────
// Master at 0.7 gives 3dB headroom vs full digital.
//
// The SFX bus sits well below the music bus on purpose — the music is the
// emotional bed of the experience, SFX are the punctuation. SFX peaks still
// register because of their attack envelopes, but their RMS volume stays
// quiet enough that the melody is always the dominant element.
const MASTER_GAIN_DEFAULT = 0.7;
const VOLUME_KEY = 'cellsnake:masterVolume';
function loadVolume(): number {
  try {
    // CRITICAL: `localStorage.getItem` returns `null` for missing keys, and
    // `Number(null) === 0` silently passes the 0..1 validation below — which
    // made first-time visitors load with volume=0 (silent audio). Always
    // bail on null before parsing.
    const raw = localStorage.getItem(VOLUME_KEY);
    if (raw === null) return MASTER_GAIN_DEFAULT;
    const v = Number(raw);
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : MASTER_GAIN_DEFAULT;
  } catch { return MASTER_GAIN_DEFAULT; }
}
function saveVolume(v: number): void {
  try { localStorage.setItem(VOLUME_KEY, String(v)); } catch { /* noop */ }
}
const SFX_BUS_GAIN   = 0.22;  // dropped further — music must dominate the mix
const MUSIC_BUS_GAIN_DEFAULT = 0.95;  // baseline; user-adjustable via setMusicVolume
const MUSIC_VOL_KEY  = 'cellsnake:musicVolume';
const MUSIC_MUTED_KEY = 'cellsnake:musicMuted';
function loadMusicVolume(): number {
  try {
    const raw = localStorage.getItem(MUSIC_VOL_KEY);
    if (raw === null) return MUSIC_BUS_GAIN_DEFAULT;
    const v = Number(raw);
    return Number.isFinite(v) && v >= 0 && v <= 1 ? v : MUSIC_BUS_GAIN_DEFAULT;
  } catch { return MUSIC_BUS_GAIN_DEFAULT; }
}
function saveMusicVolume(v: number): void {
  try { localStorage.setItem(MUSIC_VOL_KEY, String(v)); } catch { /* noop */ }
}
function loadMusicMuted(): boolean {
  try { return localStorage.getItem(MUSIC_MUTED_KEY) === '1'; } catch { return false; }
}
function saveMusicMuted(m: boolean): void {
  try { localStorage.setItem(MUSIC_MUTED_KEY, m ? '1' : '0'); } catch { /* noop */ }
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  /** Reverb input — music routes here, output goes to musicBus. */
  private reverbIn: GainNode | null = null;
  private muted = loadMuted();
  /** Master volume 0..1 — persists across sessions, applied to `this.master.gain`. */
  private volume = loadVolume();
  /** Music sub-mix volume 0..1 — independent of master so the player can
   *  duck music while keeping SFX, and vice-versa. */
  private musicVolume = loadMusicVolume();
  /** Music-only mute (master mute trumps this). */
  private musicMuted = loadMusicMuted();
  private readonly music = new Music();

  // Continuous boost noise — one persistent node pair while held.
  private boostOsc: OscillatorNode | null = null;
  private boostFilter: BiquadFilterNode | null = null;
  private boostGain: GainNode | null = null;

  // ───────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ───────────────────────────────────────────────────────────────────────────

  /** Idempotent — call from any user gesture. */
  unlock(): void {
    if (typeof window === 'undefined') return;
    if (this.ctx) {
      if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
      if (!this.muted && !this.music.isPlaying() && this.reverbIn) {
        this.music.start(this.ctx, this.reverbIn);
      }
      return;
    }
    const Ctx: typeof AudioContext | undefined =
      window.AudioContext ??
      ((window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!Ctx) return;
    const ctx = new Ctx();
    this.ctx = ctx;

    // Build the mixer graph.
    this.master = ctx.createGain();
    this.master.gain.value = this.muted ? 0 : this.volume;
    this.master.connect(ctx.destination);

    this.sfxBus = ctx.createGain();
    this.sfxBus.gain.value = SFX_BUS_GAIN;
    this.sfxBus.connect(this.master);

    this.musicBus = ctx.createGain();
    this.musicBus.gain.value = this.musicMuted ? 0 : this.musicVolume;
    this.musicBus.connect(this.master);

    // Music's signal chain goes through a reverb bus, then into musicBus.
    this.reverbIn = this.buildReverb(ctx, this.musicBus);

    ctx.resume().catch(() => {});

    if (!this.muted) this.music.start(ctx, this.reverbIn);
  }

  /**
   * Build a parallel-delay reverb. Two delays with self-feedback at different
   * times + lowpass each → produces a warm, slightly diffuse wash that fits
   * MapleStory-style melodies without smearing them into mud.
   *
   * Returns the INPUT node; routes wet+dry into `output`.
   */
  private buildReverb(ctx: AudioContext, output: AudioNode): GainNode {
    const input = ctx.createGain();
    // Dry path — direct, mostly unaltered signal.
    const dry = ctx.createGain();
    dry.gain.value = 0.78;
    input.connect(dry);
    dry.connect(output);

    // Wet bus — both delays mix into here.
    const wet = ctx.createGain();
    wet.gain.value = 0.40;
    wet.connect(output);

    // Delay 1 — short pre-delay, brighter (higher LP cutoff)
    const d1 = ctx.createDelay(2);
    d1.delayTime.value = 0.11;
    const fb1 = ctx.createGain();
    fb1.gain.value = 0.42;
    const lp1 = ctx.createBiquadFilter();
    lp1.type = 'lowpass';
    lp1.frequency.value = 2800;
    input.connect(d1);
    d1.connect(lp1);
    lp1.connect(wet);
    lp1.connect(fb1);
    fb1.connect(d1);

    // Delay 2 — longer pre-delay, darker
    const d2 = ctx.createDelay(2);
    d2.delayTime.value = 0.19;
    const fb2 = ctx.createGain();
    fb2.gain.value = 0.40;
    const lp2 = ctx.createBiquadFilter();
    lp2.type = 'lowpass';
    lp2.frequency.value = 2100;
    input.connect(d2);
    d2.connect(lp2);
    lp2.connect(wet);
    lp2.connect(fb2);
    fb2.connect(d2);

    return input;
  }

  isLocked(): boolean {
    return !this.ctx || this.ctx.state !== 'running';
  }

  /** Master volume 0..1. Persists immediately. */
  setVolume(v: number): void {
    const clamped = Math.max(0, Math.min(1, v));
    this.volume = clamped;
    saveVolume(clamped);
    if (this.master && !this.muted) {
      this.master.gain.value = clamped;
    }
  }

  /** Current master volume (post-clamp). */
  getVolume(): number { return this.volume; }

  /** Music sub-mix volume 0..1. Independent of master + SFX. */
  setMusicVolume(v: number): void {
    const clamped = Math.max(0, Math.min(1, v));
    this.musicVolume = clamped;
    saveMusicVolume(clamped);
    if (this.musicBus && !this.musicMuted) {
      this.musicBus.gain.value = clamped;
    }
  }
  getMusicVolume(): number { return this.musicVolume; }

  /** Music-only mute. Doesn't affect SFX. Master mute trumps this. */
  setMusicMuted(m: boolean): void {
    this.musicMuted = m;
    saveMusicMuted(m);
    if (this.musicBus) {
      this.musicBus.gain.value = m ? 0 : this.musicVolume;
    }
    // If unmuting and the audio engine isn't muted overall, restart the
    // music if it had been stopped.
    if (!m && !this.muted && this.ctx && this.reverbIn && !this.music.isPlaying()) {
      this.music.start(this.ctx, this.reverbIn);
    } else if (m && this.music.isPlaying()) {
      this.music.stop();
    }
  }
  isMusicMuted(): boolean { return this.musicMuted; }

  /** Public ducking — used by GameEngine to dip music during the death
   *  recap cinematic so the overlay reads cleanly. */
  duckMusic(amount: number, duration: number): void {
    this.duck(amount, duration);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master) this.master.gain.value = muted ? 0 : this.volume;
    saveMuted(muted);
    if (muted) {
      this.music.stop();
    } else if (this.ctx && this.reverbIn && !this.music.isPlaying()) {
      this.music.start(this.ctx, this.reverbIn);
    }
  }
  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }
  get isMuted(): boolean { return this.muted; }

  /** Forward to the Music engine — 0..1, drives vertical re-orchestration. */
  setMusicIntensity(t: number): void {
    this.music.setIntensity(t);
  }

  /**
   * Side-chain duck: briefly attenuate the music bus when a big SFX fires
   * so the impact moment isn't masked by the bed track. Modeled after the
   * compressor-driven ducking in modern game mixes — but done manually so
   * we don't pay for a DynamicsCompressor on every event.
   */
  private duck(amount: number, duration: number): void {
    if (!this.musicBus || !this.ctx) return;
    const now = this.ctx.currentTime;
    const base = this.musicMuted ? 0 : this.musicVolume;
    this.musicBus.gain.cancelScheduledValues(now);
    this.musicBus.gain.setValueAtTime(this.musicBus.gain.value, now);
    this.musicBus.gain.linearRampToValueAtTime(base * (1 - amount), now + 0.02);
    this.musicBus.gain.linearRampToValueAtTime(base, now + duration);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Eats — routed through sfxBus
  // ───────────────────────────────────────────────────────────────────────────

  eat(hue: number): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const basePitch = 580 + (hue / 360) * 200;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = basePitch;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.10, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.20);
    osc.connect(gain).connect(this.sfxBus);
    osc.start(now);
    osc.stop(now + 0.22);
  }

  eatPerfect(hue: number): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const basePitch = 520 + (hue / 360) * 180;
    const ratios = [1, 1.26, 1.5, 2];
    ratios.forEach((r, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = basePitch * r;
      const t0 = now + i * 0.05;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.14, t0 + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.38);
      osc.connect(gain).connect(this.sfxBus!);
      osc.start(t0);
      osc.stop(t0 + 0.4);
    });
    this.duck(0.10, 0.30);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Boost — continuous, slightly quieter so music breathes
  // ───────────────────────────────────────────────────────────────────────────

  boostStart(): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    if (this.boostOsc) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    this.boostOsc = ctx.createOscillator();
    this.boostFilter = ctx.createBiquadFilter();
    this.boostGain = ctx.createGain();
    this.boostOsc.type = 'sawtooth';
    this.boostOsc.frequency.value = 64;
    this.boostFilter.type = 'lowpass';
    this.boostFilter.frequency.value = 320;
    this.boostFilter.Q.value = 4;
    this.boostGain.gain.setValueAtTime(0, now);
    this.boostGain.gain.linearRampToValueAtTime(0.08, now + 0.06);
    this.boostOsc.connect(this.boostFilter).connect(this.boostGain).connect(this.sfxBus);
    this.boostOsc.start(now);
  }

  boostStop(): void {
    if (!this.ctx || !this.boostOsc || !this.boostGain) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const osc = this.boostOsc;
    const gain = this.boostGain;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(0, now + 0.12);
    osc.stop(now + 0.14);
    this.boostOsc = null;
    this.boostFilter = null;
    this.boostGain = null;
  }

  boostRelease(): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const filt = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(180, now);
    osc.frequency.exponentialRampToValueAtTime(50, now + 0.18);
    filt.type = 'lowpass';
    filt.frequency.value = 600;
    filt.Q.value = 1.5;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.05, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    osc.connect(filt).connect(gain).connect(this.sfxBus);
    osc.start(now);
    osc.stop(now + 0.20);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Lifecycle SFX
  // ───────────────────────────────────────────────────────────────────────────

  spawn(hue: number): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const basePitch = 320 + (hue / 360) * 160;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(basePitch * 0.5, now);
    osc.frequency.exponentialRampToValueAtTime(basePitch * 1.6, now + 0.18);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.12, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.30);
    osc.connect(gain).connect(this.sfxBus);
    osc.start(now);
    osc.stop(now + 0.32);
  }

  milestone(hue: number): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const basePitch = 540 + (hue / 360) * 220;
    const ratios = [1.5, 1.26, 1];
    ratios.forEach((r, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = basePitch * r;
      const t0 = now + i * 0.07;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.13, t0 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.45);
      osc.connect(gain).connect(this.sfxBus!);
      osc.start(t0);
      osc.stop(t0 + 0.5);
    });
    this.duck(0.18, 0.5);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Powerups
  // ───────────────────────────────────────────────────────────────────────────

  powerup(kind: 'surge' | 'shield' | 'double'): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const root = kind === 'surge' ? 540 : kind === 'double' ? 600 : 480;
    const ratios = [1, 1.5, 2];
    ratios.forEach((r, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      // Sine reads cleaner than triangle for short bell tones — switched.
      osc.type = 'sine';
      osc.frequency.value = root * r;
      const t0 = now + i * 0.06;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.16, t0 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.32);
      osc.connect(gain).connect(this.sfxBus!);
      osc.start(t0);
      osc.stop(t0 + 0.34);
    });
    this.duck(0.20, 0.45);
  }

  shieldBreak(): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(900, now);
    osc.frequency.exponentialRampToValueAtTime(380, now + 0.2);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.12, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    osc.connect(gain).connect(this.sfxBus);
    osc.start(now);
    osc.stop(now + 0.24);
    this.duck(0.16, 0.35);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Tongue, eject, kill, death
  // ───────────────────────────────────────────────────────────────────────────

  /** Quick 80ms "ping" — used by the engine to warn that a much-bigger
   *  rival has just entered close-collision range. Two soft sine tones
   *  in a tritone interval so it reads as "uh-oh" without being a siren. */
  approachWarning(): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const notes = [392, 277.18]; // G4 + C#4 — tritone, "danger" interval
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.05, now + 0.005 + i * 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22 + i * 0.04);
      osc.connect(gain).connect(this.sfxBus!);
      osc.start(now);
      osc.stop(now + 0.25 + i * 0.04);
    });
  }

  tongueHiss(): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const len = Math.floor(ctx.sampleRate * 0.08);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      const t = i / len;
      data[i] = (Math.random() * 2 - 1) * (1 - t);
    }
    const noise = ctx.createBufferSource();
    const filt = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    noise.buffer = buf;
    filt.type = 'bandpass';
    filt.frequency.value = 4800;
    filt.Q.value = 2.5;
    gain.gain.value = 0.025;
    noise.connect(filt).connect(gain).connect(this.sfxBus);
    noise.start(now);
  }

  eject(hue: number): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const basePitch = 320 + (hue / 360) * 120;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(basePitch * 1.3, now);
    osc.frequency.exponentialRampToValueAtTime(basePitch * 0.7, now + 0.10);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.08, now + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
    osc.connect(gain).connect(this.sfxBus);
    osc.start(now);
    osc.stop(now + 0.16);
  }

  death(): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;

    // Sub bass sweep — slightly shorter than before so it doesn't sit on the bed
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(620, now);
    osc.frequency.exponentialRampToValueAtTime(55, now + 0.6);
    oscGain.gain.setValueAtTime(0, now);
    oscGain.gain.linearRampToValueAtTime(0.20, now + 0.02);
    oscGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);
    osc.connect(oscGain).connect(this.sfxBus);
    osc.start(now);
    osc.stop(now + 0.72);

    // Noise burst — bandpassed
    const len = Math.floor(ctx.sampleRate * 0.35);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 1.6);
    }
    const noise = ctx.createBufferSource();
    const noiseFilter = ctx.createBiquadFilter();
    const noiseGain = ctx.createGain();
    noise.buffer = buf;
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.value = 600;
    noiseFilter.Q.value = 0.6;
    noiseGain.gain.value = 0.14;
    noise.connect(noiseFilter).connect(noiseGain).connect(this.sfxBus);
    noise.start(now);

    this.duck(0.30, 0.7);
  }

  kill(hue: number): void {
    if (!this.ctx || !this.sfxBus || this.muted) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;

    // Low thump
    const thump = ctx.createOscillator();
    const thumpGain = ctx.createGain();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(180, now);
    thump.frequency.exponentialRampToValueAtTime(50, now + 0.15);
    thumpGain.gain.setValueAtTime(0, now);
    thumpGain.gain.linearRampToValueAtTime(0.22, now + 0.005);
    thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    thump.connect(thumpGain).connect(this.sfxBus);
    thump.start(now);
    thump.stop(now + 0.2);

    // Bright tail
    const basePitch = 720 + (hue / 360) * 200;
    const tail = ctx.createOscillator();
    const tailGain = ctx.createGain();
    tail.type = 'sine';
    tail.frequency.setValueAtTime(basePitch, now + 0.04);
    tail.frequency.exponentialRampToValueAtTime(basePitch * 0.5, now + 0.32);
    tailGain.gain.setValueAtTime(0, now + 0.04);
    tailGain.gain.linearRampToValueAtTime(0.09, now + 0.05);
    tailGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.36);
    tail.connect(tailGain).connect(this.sfxBus);
    tail.start(now + 0.04);
    tail.stop(now + 0.38);

    this.duck(0.25, 0.45);
  }
}

/**
 * Module-level singleton.
 *
 * Why singleton: the AudioContext can only be unlocked by a real user
 * gesture, and the Landing page's "Play" click IS that gesture. By
 * exporting a shared instance, the Landing component can call
 * `audio.unlock()` synchronously inside the click handler — well within
 * the gesture window — while the GameEngine (which constructs later) uses
 * the same already-unlocked context.
 *
 * State that needs reset between game sessions (boost oscillators, etc.)
 * is handled explicitly by the methods.
 */
export const audio = new AudioEngine();
